import { useMemo, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { slugify, tableOfContents } from '@/features/learning/outline';

/** Texte brut d'un titre, pour en deriver l'ancre. */
function plainText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(plainText).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    return plainText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

/**
 * Rendu du contenu pedagogique redige en Markdown (tableaux, listes, citations).
 * Renders the Markdown learning content (tables, lists, blockquotes).
 *
 * Le HTML brut n'est volontairement pas interprete : seul le Markdown
 * produit par les auteurs de contenu est rendu.
 */
export function Markdown({
  content,
  className,
  anchors = false,
}: {
  content: string;
  className?: string;
  /** Pose sur les titres les ancres attendues par le sommaire. */
  anchors?: boolean;
}) {
  // Sommaire et titres rendus doivent porter les memes ancres : les deux
  // sortent de la meme table, indexee par intitule.
  const anchorsByTitle = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!anchors) return map;
    for (const entry of tableOfContents(content)) {
      const list = map.get(entry.title) ?? [];
      list.push(entry.id);
      map.set(entry.title, list);
    }
    return map;
  }, [content, anchors]);

  // Compteur d'occurrences, reinitialise a chaque rendu.
  const used = new Map<string, number>();
  const anchorFor = (children: ReactNode): string | undefined => {
    if (!anchors) return undefined;
    const title = plainText(children).trim();
    const index = used.get(title) ?? 0;
    used.set(title, index + 1);
    return anchorsByTitle.get(title)?.[index] ?? slugify(title);
  };

  return (
    <div className={cn('prose-lesson', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => <h2 id={anchorFor(children)}>{children}</h2>,
          h3: ({ children }) => <h3 id={anchorFor(children)}>{children}</h3>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="my-5 overflow-x-auto">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
